/* AddLicenseRenderer.js */

"use strict";

const uiCore = require("@gluenetworks/steve_ui_core");
const LicensesTableComponent = require("../../components/DataTable/LicensesTableComponent");
const eventRegisterMixin = require("../../utils/eventRegisterMixin");

const TextareaControl = uiCore.Control.Textfield.Textarea;
const ButtonControl = uiCore.Control.Action.Button;

const MODAL_TITLE = "Paste a Gluware license key";
const LICENSE_KEY_LABEL = "enter license key";
const LICENSE_KEY_TOOLTIP = "Paste a Gluware license key";

/**
 * @class AddLicenseRenderer
 *
 * @listens AddLicenseComponent#event:Data
 * @listens AddLicenseComponent#event:Error
 */
class AddLicenseRenderer extends eventRegisterMixin() {
    constructor(model) {
        super();
        /**
         * @type {AddLicenseComponent}
         */
        this._model = model;

        this._rootElem = this._createRootElement();

        this._validateButton = new ButtonControl({
            label: "Validate",
            title: "Verify the license details are accurate",
            darkTheme: true,
            disabled: true
        });
        this._validateButton.title = "Verify the license details are accurate";
        this._cancelButton = new ButtonControl({ label: "Cancel", darkTheme: true });
        this._activateButton = new ButtonControl({
            label: "Activate",
            title: "Add the license to the current organization",
            darkTheme: true,
            disabled: true,
            active: true
        });
        this._activateButton.title = "Add the license to the current organization";

        this._licenseInfo = new LicensesTableComponent({
            buttons: [this._validateButton, this._cancelButton, this._activateButton]
        });
        this._licenseKeyInput = new TextareaControl({
            darkTheme: true
        });

        this._errorContainer = document.createElement("div");
        this._errorContainer.classList.add("error");

        this._registerListeners();
        this.render();
    }

    get rootElement() {
        return this.getRootElement();
    }

    get model() {
        return this.getModel();
    }

    /*******************************************/
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    render() {
        if (this._renderProm === null) {
            return Promise.resolve();
        }
        this._renderProm = this._renderProm || this._render();
        return this._renderProm;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    getRootElement() {
        return this._rootElem;
    }

    getModel() {
        return this._model;
    }

    /********************************************/
    /* END PUBLIC COMPONENT RENDERER METHODS    */
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._registerListener(this._model, "Data", this._onData.bind(this));
        this._registerListener(this._model, "Error", this._onError.bind(this));
        this._registerListener(this._cancelButton, "UserClick", () => this._model.closePopUp());
        this._registerListener(this._validateButton, "UserClick", this._onValidate.bind(this));
        this._registerListener(this._activateButton, "UserClick", this._onActivate.bind(this));
        this._registerListener(
            this._licenseKeyInput,
            "UserValue",
            this._onLicenseKeyChange.bind(this)
        );
    }

    _unregisterListeners() {
        this._unregisterAllListeners();
    }

    _render() {
        this._rootElem.innerHTML = "";
        const root = document.createDocumentFragment();
        const title = document.createElement("h2");
        const label = document.createElement("label");
        label.innerText = LICENSE_KEY_LABEL;
        label.setAttribute('title', LICENSE_KEY_TOOLTIP);
        title.innerText = MODAL_TITLE;

        root.appendChild(title);
        root.appendChild(label);
        root.appendChild(this._licenseKeyInput.renderer.rootElement);
        root.appendChild(this._licenseInfo.rootElement);
        root.appendChild(this._errorContainer);
        setTimeout(this.focusInput.bind(this), 0);
        return new Promise((resolve /*,reject*/) => {
            this._rootElem.appendChild(root);
            resolve();
        });
    }

    _shutdown() {
        this._unregisterListeners();
        this._rootElem.remove();
        this._rootElem = null;

        return Promise.all([
            this._validateButton.shutdown(),
            this._cancelButton.shutdown(),
            this._activateButton.shutdown(),
            this._licenseInfo.shutdown()
        ])
            .then(() => {
                this._model = null;
                this._shutdownProm = null;
                this._renderProm = null;
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    /********************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS   */
    /* START PRIVATE RENDERER METHODS           */
    /********************************************/

    _createRootElement() {
        const rootElement = document.createElement("div");
        rootElement.classList.add("add-new-license", "modal_feedback_container");
        rootElement.component = this._model;
        return rootElement;
    }

    /********************************************/
    /* END PRIVATE RENDERER METHODS             */
    /* START HANDLER METHODS                    */
    /********************************************/
    _onData(data) {
        this._licenseInfo.setData((data && data.license) || []);
        this._checkButtons();
    }

    _onError(errorMsg) {
        this._setError(errorMsg);
    }

    _onLicenseKeyChange() {
        this._setError("");
        this._checkButtons();
    }

    _onValidate() {
        const key = this._licenseKeyInput.getValue();
        this._licenseInfo.setLoading(true);
        this._setError("");
        this._model.validate(key);
    }

    _onActivate() {
        this._activateButton.setDisabled(true);
        this._licenseInfo.setLoading(true);
        this._model.activate();
    }
    /********************************************/
    /* END HANDLER METHODS                      */
    /* START PRIVATE API METHODS                */
    /********************************************/

    _checkButtons() {
        this._validateButton.setDisabled(!this._licenseKeyInput.getValue());
        this._cancelButton.setDisabled(false);
        this._activateButton.setDisabled(!this._model.getData());
    }

    _setError(msg) {
        this._errorContainer.innerText = msg;
    }
    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /* START PUBLIC API METHODS                */
    /********************************************/

    focusInput() {
        this._licenseKeyInput.focus();
    }

    /*******************************************/
    /*  END PUBLIC API METHODS                */
    /*******************************************/
}

module.exports = AddLicenseRenderer;
